/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { oktaResourceConnectionsConfirm } from "../funcs/oktaResourceConnectionsConfirm.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ConfirmOktaResourceConnectionsResult } from "../models/components/confirmoktaresourceconnectionsresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ConfirmOktaResourceConnectionsRequest,
  ConfirmOktaResourceConnectionsSecurity,
} from "../models/operations/confirmoktaresourceconnections.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ConfirmOktaResourceConnectionsMutationVariables = {
  request: ConfirmOktaResourceConnectionsRequest;
  security?: ConfirmOktaResourceConnectionsSecurity | undefined;
  options?: RequestOptions;
};

export type ConfirmOktaResourceConnectionsMutationData =
  ConfirmOktaResourceConnectionsResult;

export type ConfirmOktaResourceConnectionsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * confirm oktaResourceConnections
 *
 * @remarks
 * Record that the administrator created the agent-to-resource connections for these servers in the identity provider console, with the audience each resource app is configured with. Servers that share an upstream share one Okta resource connection. Confirmation is the administrator's word; Speakeasy cannot check it. The connection must be verified or degraded. Requires org:admin and the okta-connections rollout.
 */
export function useConfirmOktaResourceConnectionsMutation(
  options?: MutationHookOptions<
    ConfirmOktaResourceConnectionsMutationData,
    ConfirmOktaResourceConnectionsMutationError,
    ConfirmOktaResourceConnectionsMutationVariables
  >,
): UseMutationResult<
  ConfirmOktaResourceConnectionsMutationData,
  ConfirmOktaResourceConnectionsMutationError,
  ConfirmOktaResourceConnectionsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildConfirmOktaResourceConnectionsMutation(client, options),
    ...options,
  });
}

export function mutationKeyConfirmOktaResourceConnections(): MutationKey {
  return ["@gram/client", "oktaResourceConnections", "confirm"];
}

export function buildConfirmOktaResourceConnectionsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ConfirmOktaResourceConnectionsMutationVariables,
  ) => Promise<ConfirmOktaResourceConnectionsMutationData>;
} {
  return {
    mutationKey: mutationKeyConfirmOktaResourceConnections(),
    mutationFn: function confirmOktaResourceConnectionsMutationFn({
      request,
      security,
      options,
    }): Promise<ConfirmOktaResourceConnectionsMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(oktaResourceConnectionsConfirm(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
